import Button from "@/common/components/Button";
import { DateInput } from "@/common/components/DateInput/DateInput";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TimeInput from "@/common/components/TimeInput";
import { UnitTextField } from "@/common/components/UnitTextField";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useMaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAccept } from "@/features/maintenance/features/workflows/hooks/forwardings/establishmentChoice/manualEntryProposals/useMaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAccept";
import { manualEntryProposalsAcceptSchema, ManualEntryProposalsAcceptValidationSchema } from "@/features/maintenance/features/workflows/schemas/forwardings/establishmentChoice/manualEntryProposals/manualEntryProposalsAccept.schema";
import { MaintenanceForwardingData } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

interface MaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAcceptProps {
  forwarding: MaintenanceForwardingData;
  triggerLabel?: string;
  onAccepted?: () => void;
}

const MaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAccept = ({
  forwarding,
  triggerLabel = "Fazer proposta",
  onAccepted,
}: MaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAcceptProps) => {
  const { id } = useParams();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    control,
    reset,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm<ManualEntryProposalsAcceptValidationSchema>({
    resolver: zodResolver(manualEntryProposalsAcceptSchema),
  });

  const {
    mutate: establishmentAccept,
    isPending: establishmentAcceptIsPending,
  } = useMaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAccept({
    establishmentChoiceEntryId: forwarding.establishment_choice_entry?.actual_proposal?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', id] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwarding.id] });

        setIsOpen(false);

        onAccepted?.();
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({ setMessages })(error);
      },
    },
  });

  const onSubmit: SubmitHandler<ManualEntryProposalsAcceptValidationSchema> = (data) => establishmentAccept(data);

  useEffect(() => {
    reset();
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            operationKlassToCheckPermission="Establishment::Maintenance::EstablishmentChoice::ManualEntryProposals::EstablishmentAccept"
          >
            {triggerLabel}
          </Button>
        )}
        showClose={false}
        title="Fazer proposta"
        subtitle="Sugira uma data e horario para que o cliente traga o equipamento até o seu estabelecimento."
        titleClass="text-2xl"
        subtitleClassName="mt-2 text-white-400"
        bodyClassName="text-white-800"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        confirmButtonIsLoading={establishmentAcceptIsPending}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              <DateInput
                fieldName="start_at_date"
                control={control}
                label="Data sugerida"
                className="flex-1"
                markAsRequired
                errorMessage={errors.start_at_date?.message}
              />

              <TimeInput
                fieldName="start_at_time"
                control={control}
                label="Horário sugerido"
                markAsRequired
                className="flex-1"
                errorMessage={errors.start_at_time?.message}
              />
            </div>

            <Controller
              control={control}
              name="deadline_to_complete_in_days"
              render={({ field }) => (
                <UnitTextField
                  decimals={0}
                  placeholder="Escreva aqui o prazo em dias"
                  unit="dia(s)"
                  label="Prazo em dias para envio do diagnostico e orçamento pós-check-in"
                  markAsRequired
                  {...field}
                  errorMessage={errors.deadline_to_complete_in_days?.message}
                />
              )}
            />
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default MaintenanceEstablishmentChoiceManualEntryProposalEstablishmentAccept;
